import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { lifecycleDateText } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader, SectionHeader } from '../components/States.tsx';
import { loadDeletions, restoreProject, type ProjectDeletion } from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

const DELETED_WORDS =
  'Projects with field data stay here for 30 days, hidden everywhere, before they are removed for good with their files.';

/** How long removed projects stay listed, for the record. */
const RECORD_DAYS = 90;

/**
 * Deleted projects, the Projects page's "Deleted" list since C4 (tenant admins, decision 417; docs/specs/cloud/project-lifecycle.md §5): the Deleted bin, each
 * project with who deleted it, when, and the day it is removed for good, with "Restore"; and the projects removed in
 * the last 90 days, for the record.
 */
export function DeletedProjectsScreen({
  sb,
  tenantId,
  embedded = false,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** Shown as the Projects page's "Deleted" list (C4, decision 844): no page title of its own. */
  readonly embedded?: boolean;
}): ReactElement {
  const [rows, setRows] = useState<ProjectDeletion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    loadDeletions(sb, tenantId)
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(load, [load]);

  const restore = async (d: ProjectDeletion): Promise<void> => {
    setBusy(d.id);
    setNotice(null);
    try {
      await restoreProject(sb, d.projectId);
      setNotice(`${d.number} is restored. Laptops sync it again.`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const since = Date.now() - RECORD_DAYS * 86_400_000;
  const inBin = rows?.filter((d) => d.outcome === 'binned' && !d.restoredAt && !d.purgedAt) ?? [];
  const removed =
    rows?.filter(
      (d) =>
        !d.restoredAt &&
        (d.purgedAt !== null || d.outcome === 'deleted') &&
        Date.parse(d.purgedAt ?? d.deletedAt) >= since,
    ) ?? [];

  return (
    <section className={embedded ? 'stack' : 'page'}>
      {embedded ? (
        <SectionHeader title="Deleted projects" subtitle={DELETED_WORDS} />
      ) : (
        <PageHeader title="Deleted projects" subtitle={DELETED_WORDS} />
      )}
      {notice && (
        <div className="notice" role="status">
          <p className="body">{notice}</p>
        </div>
      )}
      {error && <ErrorState message={error} onRetry={load} />}
      {!rows && !error && <Loading what="deleted projects" />}
      {rows && inBin.length === 0 && (
        <div className="panel">
          <p className="body">The Deleted bin is empty.</p>
        </div>
      )}
      {inBin.length > 0 && (
        <TableWrap>
          <table className="table" aria-label="In the Deleted bin">
            <thead>
              <tr>
                <th className="label">Project</th>
                <th className="label">Title</th>
                <th className="label">Deleted</th>
                <th className="label">By</th>
                <th className="label">Removed for good on</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {inBin.map((d) => (
                <tr key={d.id}>
                  <td className="data">{d.number}</td>
                  <td>{d.title}</td>
                  <td className="data">{lifecycleDateText(d.deletedAt)}</td>
                  <td>{d.deletedBy ?? 'Unknown'}</td>
                  <td className="data">{lifecycleDateText(d.purgeAfter)}</td>
                  <td className="table__actions">
                    <Button
                      busy={busy === d.id}
                      aria-label={`Restore ${d.number}`}
                      onClick={() => void restore(d)}
                    >
                      {busy === d.id ? 'Restoring…' : 'Restore'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      {removed.length > 0 && (
        <>
          <h2 className="heading">Removed in the last {RECORD_DAYS} days</h2>
          <TableWrap>
            <table className="table" aria-label="Removed for good">
              <thead>
                <tr>
                  <th className="label">Project</th>
                  <th className="label">Title</th>
                  <th className="label">Deleted</th>
                  <th className="label">By</th>
                  <th className="label">How</th>
                </tr>
              </thead>
              <tbody>
                {removed.map((d) => (
                  <tr key={d.id}>
                    <td className="data">{d.number}</td>
                    <td>{d.title}</td>
                    <td className="data">{lifecycleDateText(d.deletedAt)}</td>
                    <td>{d.deletedBy ?? 'Unknown'}</td>
                    <td>
                      {d.outcome === 'deleted'
                        ? 'At once (no field data)'
                        : `After 30 days in the bin${d.purgedAt ? `, ${lifecycleDateText(d.purgedAt)}` : ''}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}
    </section>
  );
}
